import { useState } from "react";
import { T, inputStyle, LANGUAGES } from "../theme";
import Spinner from "../components/Spinner";

export default function LoginScreen({ onSignIn, onSignUp }) {
  const [tab, setTab] = useState("login");
  const [lang, setLang] = useState("en");
  const [form, setForm] = useState({
    username: "",
    email: "",
    password: "",
    display: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handle = async () => {
    setLoading(true);
    setError(null);
    try {
      if (tab === "login") {
        await onSignIn(form.email, form.password);
      } else {
        if (!form.username.trim()) {
          throw new Error("Username is required");
        }
        await onSignUp({
          email: form.email,
          password: form.password,
          username: form.username.trim().toLowerCase(),
          displayName: form.display.trim() || form.username.trim(),
          preferredLanguage: lang,
        });
      }
    } catch (err) {
      setError(err.message || "Something went wrong");
    }
    setLoading(false);
  };

  return (
    <div
      style={{
        minHeight: "100%",
        background: T.bg0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "calc(24px + env(safe-area-inset-top, 0px)) 24px 24px",
      }}
    >
      {/* Logo */}
      <div style={{ marginBottom: 32, textAlign: "center" }}>
        <div
          style={{
            fontFamily: "'Barlow Condensed', sans-serif",
            fontSize: 42,
            fontWeight: 900,
            color: T.text,
            letterSpacing: -1,
            lineHeight: 1,
          }}
        >
          CREW<span style={{ color: T.orange }}>LINK</span>
        </div>
        <div
          style={{
            color: T.textDim,
            fontSize: 13,
            marginTop: 6,
            letterSpacing: 1,
            textTransform: "uppercase",
          }}
        >
          Job Site Translator
        </div>
      </div>

      {/* Tab switcher */}
      <div
        style={{
          display: "flex",
          marginBottom: 24,
          background: T.bg2,
          borderRadius: 8,
          padding: 3,
          width: "100%",
          maxWidth: 340,
        }}
      >
        {["login", "signup"].map((t) => (
          <button
            key={t}
            onClick={() => { setTab(t); setError(null); }}
            style={{
              flex: 1,
              padding: "9px 0",
              background: tab === t ? T.orange : "transparent",
              color: tab === t ? "#fff" : T.textDim,
              border: "none",
              borderRadius: 6,
              fontWeight: 700,
              fontSize: 13,
              textTransform: "uppercase",
              letterSpacing: 1,
              transition: "all 0.2s",
            }}
          >
            {t === "login" ? "Sign In" : "Create Account"}
          </button>
        ))}
      </div>

      {/* Form */}
      <div
        style={{
          width: "100%",
          maxWidth: 340,
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        {tab === "signup" && (
          <input
            placeholder="Display Name"
            value={form.display}
            onChange={(e) => setForm((p) => ({ ...p, display: e.target.value }))}
            style={inputStyle}
          />
        )}
        {tab === "signup" && (
          <input
            placeholder="Username"
            value={form.username}
            onChange={(e) => setForm((p) => ({ ...p, username: e.target.value }))}
            style={inputStyle}
            autoCapitalize="none"
          />
        )}
        <input
          placeholder="Email"
          type="email"
          value={form.email}
          onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
          style={inputStyle}
          autoCapitalize="none"
        />
        <input
          placeholder="Password"
          type="password"
          value={form.password}
          onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
          style={inputStyle}
        />

        {/* Language selector (signup only) */}
        {tab === "signup" && (
          <div>
            <div
              style={{
                fontSize: 11,
                color: T.textDim,
                marginBottom: 6,
                textTransform: "uppercase",
                letterSpacing: 1,
              }}
            >
              My Language
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {Object.entries(LANGUAGES).map(([code, cfg]) => (
                <button
                  key={code}
                  onClick={() => setLang(code)}
                  style={{
                    padding: "12px 0",
                    background: lang === code ? cfg.color : T.bg2,
                    border: `2px solid ${lang === code ? "transparent" : T.border}`,
                    borderRadius: 8,
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: 14,
                    transition: "all 0.15s",
                  }}
                >
                  {cfg.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div
            style={{
              color: T.danger,
              fontSize: 13,
              textAlign: "center",
              padding: "8px 12px",
              background: "rgba(239,68,68,0.1)",
              borderRadius: 6,
            }}
          >
            {error}
          </div>
        )}

        {/* Submit */}
        <button
          onClick={handle}
          disabled={loading}
          style={{
            padding: "16px 0",
            background: T.orange,
            border: "none",
            borderRadius: 8,
            color: "#fff",
            fontWeight: 900,
            fontSize: 16,
            letterSpacing: 0.5,
            marginTop: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            opacity: loading ? 0.7 : 1,
          }}
        >
          {loading ? (
            <><Spinner /> Connecting...</>
          ) : tab === "login" ? (
            "SIGN IN"
          ) : (
            "CREATE ACCOUNT"
          )}
        </button>
      </div>
    </div>
  );
}
